import React from 'react';

export type PaymentOption = {
  id?: string;
  key?: string;
  value: string;
};

export type PriceBreakdownLine = {
  label: string;
  amount: string;
};

export type PaymentSectionData = {
  showPayment?: boolean;
  selectedPaymentLabel?: string;
  paymentOptions?: PaymentOption[];
  paymentDropdownOpen?: boolean;
  onTogglePaymentDropdown?: () => void;
  onSelectPayment?: (option: PaymentOption) => void;
  showStartDate?: boolean;
  startDateLabel?: string;
  startDateValue?: string;
  minStartDate?: string;
  maxStartDate?: string;
  onStartDateChange?: (value: string) => void;
  showPriceBreakdown?: boolean;
  priceBreakdownLines?: PriceBreakdownLine[];
};

type Props = Readonly<{
  data: PaymentSectionData;
}>;

const unstyledButtonStyle: React.CSSProperties = {
  display: 'flex',
  width: '100%',
  alignItems: 'center',
  justifyContent: 'space-between',
  background: 'none',
  border: 'none',
  padding: 0,
  margin: 0,
  font: 'inherit',
  color: 'inherit',
  textAlign: 'inherit',
  cursor: 'pointer',
};

export function StartDateSection({ data }: Props) {
  const {
    showStartDate,
    startDateLabel,
    startDateValue,
    minStartDate,
    maxStartDate,
    onStartDateChange,
  } = data;

  if (!showStartDate) {
    return null;
  }

  return (
    <div className="content-group">
      <ul className="list no-border-bottom decrease-height">
        <li>
          <div className="list-text-left">
            <p>{startDateLabel}</p>
          </div>
          <div className="list-text-right">
            <p>
              <input
                type="date"
                className="start-date-picker"
                aria-label={startDateLabel}
                value={startDateValue || ''}
                min={minStartDate}
                max={maxStartDate}
                onChange={(event) => onStartDateChange?.(event.target.value)}
              />
            </p>
          </div>
        </li>
      </ul>
    </div>
  );
}

function PriceBreakdownGroup({ data }: Props) {
  const { showPriceBreakdown, priceBreakdownLines = [] } = data;

  if (!showPriceBreakdown || priceBreakdownLines.length === 0) {
    return null;
  }

  return (
    <div className="content-group-extra booking-purchase-price-breakdown">
      <ul className="list no-bottom-border">
        {priceBreakdownLines.map((line) => (
          <li key={`${line.label}:${line.amount}`}>
            <div className="list-text-left">
              <p>{line.label}</p>
            </div>
            <div className="list-text-right">
              <p>{line.amount}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function hasPaymentOption(data: PaymentSectionData) {
  return Boolean(data.showPayment && data.selectedPaymentLabel);
}

function PaymentGroup({ data }: Props) {
  if (!hasPaymentOption(data)) {
    return null;
  }

  return (
    <div className="content-group-extra">
      <ul className="list">
        <PaymentOptionRow data={data} />
      </ul>
    </div>
  );
}

export function PaymentSection({ data }: Props) {
  if (!data) {
    return null;
  }

  return (
    <>
      <PriceBreakdownGroup data={data} />
      <PaymentGroup data={data} />
    </>
  );
}

/**
 * The payment method `<li>` on its own, without a list wrapper. The book-event
 * and book-appointment templates put this row inside the guest counter's
 * `content-group > ul.list`, so the caller owns the surrounding markup.
 *
 * The row labels must stay as `<p>` inside `.list-text-left`: `.list p` in
 * modules/lists.scss is what themes them (`rgba($textColor, 0.7)`), while
 * `.list-text-left` only sets the float. A bare label inherits its color from
 * the button instead, which left it invisible on dark studio backgrounds.
 */
export function PaymentOptionRow({ data }: Props) {
  if (!data || !hasPaymentOption(data)) {
    return null;
  }

  const {
    selectedPaymentLabel,
    paymentOptions = [],
    paymentDropdownOpen = false,
    onTogglePaymentDropdown,
    onSelectPayment,
  } = data;

  const canTogglePayment = paymentOptions.length > 1;

  const selectedLabel = (
    <div className="list-text-left">
      <p>{selectedPaymentLabel}</p>
    </div>
  );

  const caret = (
    <span className="list-text-right">
      <span
        className={
          paymentDropdownOpen ? 'icon icon-arrow-up' : 'icon icon-arrow-down'
        }
      />
    </span>
  );

  return (
    <li
      id="class-booking-payment-options-toggle"
      className={canTogglePayment ? 'clickable' : undefined}
    >
      {canTogglePayment ? (
        <button
          type="button"
          style={unstyledButtonStyle}
          onClick={onTogglePaymentDropdown}
        >
          {selectedLabel}
          {caret}
        </button>
      ) : (
        selectedLabel
      )}
      {paymentDropdownOpen ? (
        <PaymentDropdown
          options={paymentOptions}
          selectedLabel={selectedPaymentLabel}
          onSelect={onSelectPayment}
        />
      ) : null}
    </li>
  );
}

function PaymentDropdown({
  options,
  selectedLabel,
  onSelect,
}: Readonly<{
  options: PaymentOption[];
  selectedLabel?: string;
  onSelect?: (option: PaymentOption) => void;
}>) {
  return (
    <ul className="list payment-dropdown">
      {options.map((option) => (
        <li
          key={option.id || option.value}
          id={option.id ? `class-booking-payment-option-${option.id}` : undefined}
          className="clickable"
        >
          <button
            type="button"
            style={unstyledButtonStyle}
            onClick={(event) => {
              event.stopPropagation();
              onSelect?.(option);
            }}
          >
            <div className="list-text-left">
              <p className={option.value === selectedLabel ? 'active' : undefined}>
                {option.value}
              </p>
            </div>
          </button>
        </li>
      ))}
    </ul>
  );
}
